import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { apiClient } from '../lib/api-client';

interface OrgOption {
  id: string;
  name: string;
}

/**
 * Sélecteur d'organisation pour les listes transverses (rôles Adweb uniquement :
 * `/organizations` est réservé aux rôles plateforme). Simple confort d'affichage —
 * le cloisonnement reste appliqué côté serveur.
 */
export function OrganizationFilter({
  value,
  onChange,
  inHeader = false,
}: {
  value: string;
  onChange: (organizationId: string) => void;
  /** Posé dans l'en-tête de page : reprend le style des contrôles d'en-tête. */
  inHeader?: boolean;
}) {
  const { t } = useTranslation();
  const orgs = useQuery({
    queryKey: ['organizations', 'for-filter'],
    queryFn: async () =>
      (await apiClient.get<{ data: OrgOption[] }>('/organizations?pageSize=100')).data.data,
    staleTime: 60_000,
  });
  const options = [...(orgs.data ?? [])].sort((a, b) => a.name.localeCompare(b.name, 'fr'));

  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label={t('app.filterByOrganization')}
      className={
        inHeader
          ? 'hdr-ctl focus:outline-none focus:ring-2 focus:ring-brand/30'
          : 'rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30'
      }
    >
      <option value="">{t('app.allOrganizations')}</option>
      {options.map((o) => (
        <option key={o.id} value={o.id}>
          {o.name}
        </option>
      ))}
    </select>
  );
}
